<template>
    <div class="relative bg-gray-100">
        <Head :title="appPageTitle">
            <meta name="description" :content="$t('vue.app_meta_desc')" />
        </Head>

        <Navbar @handleSideBarMobile="() => {}" />

        <div
            v-if="globalProps.user.isImpersonating"
            class="fixed bottom-0 z-[500] flex w-1/2 mx-auto left-0 right-0 flex-wrap items-center justify-between bg-gray-900 bg-opacity-50 px-10 py-2"
        >
            <div class="my-1 text-sm text-white">
                {{ $t("vue.you_are_now_seeing_the_app_as") }}:
                {{ globalProps.user.name }}
                {{ $t("vue.profile_id") }}:
                {{ globalProps.user.id }}
                {{ $t("vue.account_id") }}:
                {{ globalProps.team.id }}
            </div>
            <AtomicButton size="sm" color="purple" @click="endImpersonation">
                <template #icon>
                    <EyeSlashIcon class="mr-2 h-6 w-6" />
                </template>
                {{ $t("vue.leave_impersonation") }}
            </AtomicButton>
        </div>

        <div
            class="grid min-h-screen grid-cols-1 bg-gray-100 md:grid-cols-[64px_1fr]"
            :class="
                isAiWorkspace
                    ? 'xl:grid-cols-[64px_1fr]'
                    : 'xl:grid-cols-[256px_1fr]'
            "
        >
            <div :class="{ 'bg-white': sidebarOpened }">
                <Sidebar
                    v-click-away="onClickAway"
                    :opened="sidebarOpened"
                    :compact="isAiWorkspace"
                    @close="sidebarOpened = false"
                    @open="sidebarOpened = true"
                />
            </div>
            <div
                v-if="isAiWorkspace"
                class="main-content-template w-full min-w-0 px-6 pb-6 pt-[64px] transition duration-300 ease-in-out sm:px-8 md:mt-16 md:pt-6"
                :class="{
                    'pointer-events-none md:opacity-90 md:blur-xs':
                        sidebarOpened,
                }"
            >
                <slot />
            </div>
            <div
                v-else-if="showMain"
                class="main-content-template mx-auto w-full px-5 pt-[56px] opacity-100 blur-none transition duration-300 ease-in-out sm:px-10 md:mt-16 md:pt-0 xl:max-w-[1328px] xl:opacity-100 xl:blur-none"
                :class="{
                    'pointer-events-none md:opacity-90 md:blur-xs':
                        sidebarOpened,
                }"
            >
                <slot />
            </div>
            <div
                v-else
                class="main-content-template mx-auto w-full px-6 pt-[56px] opacity-100 blur-none transition duration-300 ease-in-out sm:px-10 md:mt-16 md:pt-0 xl:opacity-100 xl:blur-none"
                :class="{
                    'pointer-events-none md:opacity-90 md:blur-xs':
                        sidebarOpened,
                }"
            >
                <slot />
            </div>
        </div>

        <ConfirmDialog></ConfirmDialog>
        <Notifications></Notifications>

        <AskAiWidget
            v-if="globalProps.aiEarlyAccessActive && pageCtx.available.value"
        />
    </div>
</template>

<script lang="ts" setup>
import { Head } from "@inertiajs/vue3";
import { computed, onMounted, ref } from "vue";
import { usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { EyeSlashIcon } from "@heroicons/vue/24/outline";
import Bugsnag from "@bugsnag/js";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import AskAiWidget from "@/components/AiChat/AskAiWidget.vue";
import { useAiPageContext } from "@/composables/useAiPageContext";

const pageCtx = useAiPageContext();
const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);

const page = usePage();
const showMain = computed(() => !page.url.startsWith("/domain-setup-wizard"));

// The AI workspace (chat / saved content / tags) is a full-bleed page with the
// app sidebar collapsed to an icon rail, so the chat gets maximum width.
const AI_WORKSPACE_PREFIXES = ["/ai-chat", "/ai-content", "/ai-tags"];
const isAiWorkspace = computed(() =>
    AI_WORKSPACE_PREFIXES.some((prefix) => page.url.startsWith(prefix)),
);

const routeTitleByPrefix: Record<string, string> = {
    "/dashboard": "Dashboard | DMARCeye",
    "/reports": "Reports | DMARCeye",
    "/domains": "Domains | DMARCeye",
    "/billing": "Billing | DMARCeye",
    "/alerts": "Alerts | DMARCeye",
    "/members": "Members | DMARCeye",
    "/teams": "Teams | DMARCeye",
    "/settings": "Settings | DMARCeye",
    "/integrations": "Integrations | DMARCeye",
    "/blacklists": "Blacklists | DMARCeye",
    "/support": "Support | DMARCeye",
    "/changelog": "Changelog | DMARCeye",
    "/checkout": "Checkout | DMARCeye",
    "/dns-tools": "DNS Tools | DMARCeye",
    "/domain-setup-wizard": "Domain Setup Wizard | DMARCeye",
    "/kiosk": "Kiosk | DMARCeye",
    "/announcements-notifications": "Announcements | DMARCeye",
    "/all-teams": "Teams | DMARCeye",
    "/members-count": "Members | DMARCeye",
    "/invitations": "Members | DMARCeye",
    "/test-language": "Test Language | DMARCeye",
    "/test-translations": "Test Translation | DMARCeye",
};

const appPageTitle = computed(() => {
    const path = page.url.split("?")[0];
    const rootPrefix = `/${path.split("/")[1] ?? ""}`;

    return (
        routeTitleByPrefix[path] ?? routeTitleByPrefix[rootPrefix] ?? "DMARCeye"
    );
});

onMounted(() => {
    const userObject = {
        id: globalProps.value.user.id,
        email: globalProps.value.user.email,
        current_team_id: teamProps.value.id,
    };
    Bugsnag.setUser(JSON.stringify(userObject));

    getData();
});

const { fetchTeams, getCurrentTeamMembersCount } = useTeamsAndMembers();

const getData = async () => {
    await getCurrentTeamMembersCount();
    await fetchTeams();
};

const sidebarOpened = ref(false);
const onClickAway = () => {
    sidebarOpened.value = false;
};
const endImpersonation = () => {
    router.delete(route("kiosk.users.nonimpersonate"), {
        onSuccess: () => {
            getData();
        },
    });
};
</script>

<style></style>
